/**
 * Create a new Step Study cohort.
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PageHeader from '../components/PageHeader';
import { usePermissions } from '../hooks/usePermissions';
import { get, post, getErrorMessage } from '../api/client';
import { MEETING_DAYS, MEETING_TIME_OPTIONS } from '../config/stepStudyConfig';

const StepStudyNewCohort = () => {
  const navigate = useNavigate();
  const { canCreateStepStudyCohort } = usePermissions();
  const [classTypes, setClassTypes] = useState([]);
  const [startDate, setStartDate] = useState('');
  const [expectedGraduationDate, setExpectedGraduationDate] = useState('');
  const [classType, setClassType] = useState('');
  const [leader1, setLeader1] = useState('');
  const [leader2, setLeader2] = useState('');
  const [leader3, setLeader3] = useState('');
  const [book, setBook] = useState('');
  const [useRoster, setUseRoster] = useState(false);
  const [meetingDay, setMeetingDay] = useState('');
  const [meetingTimeStart, setMeetingTimeStart] = useState('');
  const [meetingTimeEnd, setMeetingTimeEnd] = useState('');
  const [error, setError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!canCreateStepStudyCohort) {
      navigate('/step-study', { replace: true });
      return;
    }
    get('/api/step-study/config').then(({ ok, data }) => {
      if (ok && data?.classTypes) {
        setClassTypes(data.classTypes);
        if (data.classTypes[0]) setClassType(data.classTypes[0]);
      }
    });
  }, [canCreateStepStudyCohort, navigate]);

  useEffect(() => {
    if (!startDate) return;
    const d = new Date(startDate);
    d.setFullYear(d.getFullYear() + 1);
    setExpectedGraduationDate(d.toISOString().split('T')[0]);
  }, [startDate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!classType || !startDate || !expectedGraduationDate || !leader1.trim() || !leader2.trim()) {
      setError('Class type, dates, and both leaders are required.');
      return;
    }
    if (useRoster && (!meetingDay.trim() || !meetingTimeStart.trim() || !meetingTimeEnd.trim())) {
      setError('Meeting day and time are required when using roster.');
      return;
    }
    setIsSubmitting(true);
    try {
      const { ok, status, data } = await post('/api/step-study/cohorts', {
        classType,
        startDate,
        expectedGraduationDate,
        leader1: leader1.trim(),
        leader2: leader2.trim(),
        leader3: leader3.trim(),
        book: book.trim(),
        meetingDay: meetingDay.trim(),
        meetingTimeStart: meetingTimeStart.trim(),
        meetingTimeEnd: meetingTimeEnd.trim(),
        useRoster,
      });
      if (!ok) {
        setError(getErrorMessage({ status, data }, 'Failed to create cohort.'));
        return;
      }
      navigate(`/step-study/${data._id}`);
    } catch (err) {
      setError('Failed to create cohort.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="entry-page">
      <PageHeader title="New Step Study Cohort" backTo="/step-study" backLabel="← Dashboard" />
      <p style={{ fontSize: '0.95rem', color: 'var(--cr-text-muted)', marginBottom: '1rem', textAlign: 'center' }}>
        Create a new cohort to track attendance and progress.
      </p>

      <div style={{ backgroundColor: 'var(--cr-bg-warm)', padding: '1rem', borderRadius: '8px', marginBottom: '1rem', border: '1px solid var(--cr-border)', width: '100%', maxWidth: '420px' }}>
        <strong>Instructions:</strong>
        <ol style={{ paddingLeft: '1.2rem', margin: '0.5rem 0 0 0' }}>
          <li><strong>Choose the class type</strong> (e.g., Journey Begins Men). This identifies which curriculum the cohort is following.</li>
          <li><strong>Enter the two required leaders.</strong> Use first name plus last initial (e.g., John D., Jane S.). A third leader is optional.</li>
          <li><strong>Set the start and graduation dates.</strong> Expected graduation defaults to 12 months from start; adjust if needed.</li>
          <li><strong>Click Create Cohort</strong> to add the cohort. You can then add weekly attendance entries from the cohort page.</li>
        </ol>
      </div>

      <form onSubmit={handleSubmit} style={{ width: '100%', maxWidth: '420px' }}>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Class Type</label>
          <select value={classType} onChange={(e) => setClassType(e.target.value)} required>
            {classTypes.map((t) => (
              <option key={t} value={t}>{t}</option>
            ))}
          </select>
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Leader 1 (first name + last initial, e.g., John D.)</label>
          <input
            type="text"
            value={leader1}
            onChange={(e) => setLeader1(e.target.value)}
            placeholder="e.g., John D."
            required
          />
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Leader 2 (first name + last initial, e.g., Jane S.)</label>
          <input
            type="text"
            value={leader2}
            onChange={(e) => setLeader2(e.target.value)}
            placeholder="e.g., Jane S."
            required
          />
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Leader 3 (optional)</label>
          <input
            type="text"
            value={leader3}
            onChange={(e) => setLeader3(e.target.value)}
            placeholder="e.g., Mike T."
          />
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Book (optional – e.g., Step 1, Journey Begins)</label>
          <input
            type="text"
            value={book}
            onChange={(e) => setBook(e.target.value)}
            placeholder="e.g., Step 1"
          />
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
            <input type="checkbox" checked={useRoster} onChange={(e) => setUseRoster(e.target.checked)} />
            Use roster (track members, attendance, print roster)
          </label>
        </div>
        {useRoster && (
          <>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Meeting Day</label>
              <select value={meetingDay} onChange={(e) => setMeetingDay(e.target.value)} required>
                <option value="">Select day</option>
                {MEETING_DAYS.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Meeting Start Time</label>
              <select value={meetingTimeStart} onChange={(e) => setMeetingTimeStart(e.target.value)} required>
                <option value="">Select start</option>
                {MEETING_TIME_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="form-group" style={{ marginBottom: '1rem' }}>
              <label>Meeting End Time</label>
              <select value={meetingTimeEnd} onChange={(e) => setMeetingTimeEnd(e.target.value)} required>
                <option value="">Select end</option>
                {MEETING_TIME_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </>
        )}
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            onClick={(e) => e.target.showPicker?.()}
            required
          />
        </div>
        <div className="form-group" style={{ marginBottom: '1rem' }}>
          <label>Expected Graduation Date (default: 12 months from start)</label>
          <input
            type="date"
            value={expectedGraduationDate}
            onChange={(e) => setExpectedGraduationDate(e.target.value)}
            onClick={(e) => e.target.showPicker?.()}
            required
          />
        </div>
        {error && <p style={{ color: 'var(--cr-text)', marginBottom: '1rem', fontSize: '0.9rem' }}>{error}</p>}
        <button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Creating…' : 'Create Cohort'}
        </button>
      </form>

    </div>
  );
};

export default StepStudyNewCohort;
